import { Colors, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';

import { Between } from 'bones/components/atoms/icons/Between';
import { Tooltip } from 'bones/components/atoms/Tooltip';
import { ItemSelector } from 'bones/components/molecules/ItemSelector/ItemSelector';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { XSD_STRING_IRI } from 'bones/constants/iris';
import { getIntentClassName } from 'bones/utils/getIntentClassName';
import {
  COMPARISON_OPERATOR_TYPES_BY_DATATYPE_RANGE,
  DatatypeOperatorType,
  DatatypeOperatorTypeValue,
  DatatypeRange,
  OPERATOR_TYPES_BY_DATATYPE_RANGE,
  TEXT_BY_OPERATOR_TYPE,
} from 'bones/utils/queryBuilder/constants';
import {
  ComparisonOperatorTypeSelectItem,
  OperatorTypeSelectItem,
  createComparisonOperatorTypeSelectItem,
  createOperatorTypeSelectItem,
} from 'bones/utils/queryBuilder/helpers/select';

export interface OperatorSelectorProps {
  invalid?: boolean;
  onSelect: (type: DatatypeOperatorTypeValue, comparison: boolean) => void;
  operatorComparison?: boolean;
  operatorType?: DatatypeOperatorTypeValue;
  range?: DatatypeRange;
}

const getOperatorIconName = (operatorType: DatatypeOperatorTypeValue) => {
  switch (operatorType) {
    case DatatypeOperatorType.EXISTS:
    case DatatypeOperatorType.DOES_NOT_EXIST:
      return undefined;
    case DatatypeOperatorType.AFTER:
    case DatatypeOperatorType.GT:
      return IconNames.GREATER_THAN;
    case DatatypeOperatorType.AFTER_OR_ON:
    case DatatypeOperatorType.GTE:
      return IconNames.GREATER_THAN_OR_EQUAL_TO;
    case DatatypeOperatorType.BEFORE:
    case DatatypeOperatorType.LT:
      return IconNames.LESS_THAN;
    case DatatypeOperatorType.BEFORE_OR_ON:
    case DatatypeOperatorType.LTE:
      return IconNames.LESS_THAN_OR_EQUAL_TO;
    case DatatypeOperatorType.NOT_ON:
    case DatatypeOperatorType.DOES_NOT_EQUAL:
    case DatatypeOperatorType.DOES_NOT_CONTAIN:
      return IconNames.NOT_EQUAL_TO;
    case DatatypeOperatorType.BETWEEN:
      return <Between color={Colors.GRAY1} />;
    case DatatypeOperatorType.CONTAINS:
      return IconNames.ARRAY;
    default:
      return IconNames.EQUALS;
  }
};

export const OperatorSelector: React.VFC<OperatorSelectorProps> = ({
  invalid,
  onSelect,
  operatorComparison,
  operatorType,
  range,
}) => {
  const {
    copy,
    testIds,
    readOnly,
    comparisonDisabled,
    hiddenOperators,
    limitOperators,
  } = useQueryBuilderSchemaContext();

  const selectItems: OperatorTypeSelectItem[] =
    OPERATOR_TYPES_BY_DATATYPE_RANGE[range || XSD_STRING_IRI]
      ?.filter((operator) => !hiddenOperators?.has(operator))
      .map((operator) => createOperatorTypeSelectItem(operator)) || [];
  const selectComparisonItems =
    COMPARISON_OPERATOR_TYPES_BY_DATATYPE_RANGE[range || XSD_STRING_IRI]
      ?.filter((operator) => !hiddenOperators?.has(operator))
      .map((operator) => createComparisonOperatorTypeSelectItem(operator)) ||
    [];

  if (!comparisonDisabled && selectComparisonItems.length) {
    selectItems.unshift({
      text: copy.body.operator.comparison,
      value: copy.body.operator.comparison,
      items: selectComparisonItems,
      comparison: false,
    });
  }

  const selectedItem =
    (operatorComparison
      ? selectComparisonItems.find(
          (item) => item.comparisonValue === operatorType
        )
      : selectItems.find((item) => item.value === operatorType)) || null;

  const handleSelect = (
    item: ComparisonOperatorTypeSelectItem | OperatorTypeSelectItem | null
  ) => {
    const foundItem = item?.comparison
      ? selectComparisonItems.find(
          (i) => i.comparisonValue === item?.comparisonValue
        )
      : selectItems.find((i) => i.value === item?.value);

    // istanbul ignore else
    if (foundItem) {
      onSelect(
        foundItem.comparison
          ? (foundItem.comparisonValue as DatatypeOperatorTypeValue)
          : (foundItem.value as DatatypeOperatorTypeValue),
        foundItem.comparison
      );
    }
  };

  const operatorIcon =
    (operatorType && getOperatorIconName(operatorType)) || undefined;
  const operatorText =
    (operatorType && TEXT_BY_OPERATOR_TYPE[operatorType]) || '';

  return limitOperators ? (
    <Tooltip content={operatorText} disabled={!operatorType}>
      {operatorIcon ? (
        <Icon className="sd-query-builder-operator-icon" icon={operatorIcon} />
      ) : (
        operatorText
      )}
    </Tooltip>
  ) : (
    <ItemSelector.Single<
      ComparisonOperatorTypeSelectItem,
      OperatorTypeSelectItem
    >
      buttonClassName={classNames({
        [getIntentClassName('sd-qb-none')]: !operatorIcon && operatorText,
      })}
      buttonIcon={operatorIcon}
      buttonMinimal={Boolean(operatorType)}
      buttonRightIcon={(!operatorType && IconNames.CARET_DOWN) || null}
      buttonText={
        (!operatorType && copy.body.operator.placeholder) ||
        (!operatorIcon && operatorText) ||
        ''
      }
      className={classNames('sd-query-builder-operator-selector', {
        'sd-query-builder-operator-selector--invalid': invalid,
        'sd-query-builder-operator-selector--text-only':
          !operatorIcon && operatorText,
        'sd-query-builder-operator-selector--read-only': readOnly,
      })}
      disabled={readOnly}
      filterable
      filterText={copy.body.filter.placeholder}
      items={selectItems}
      itemTestId={testIds.operatorSelectorItem}
      onSelect={handleSelect}
      selected={selectedItem}
      subItemTestId={testIds.operatorSelectorSubItem}
      testId={testIds.operatorSelector}
      tooltipProps={{
        content: operatorText,
        disabled: !operatorIcon,
      }}
    />
  );
};
